import { smoothstep } from '../../core/math';
import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

const RIFT_ROCK = 0xa890c0;

/** Corrupted cavern kit: violet-tinted rock, rift crystals growing from the walls. */
const KIT: DungeonKit = {
  low: [
    { model: 'rock_b', scale: [0.6, 1.0], tint: RIFT_ROCK, weight: 2 },
    { model: 'rift_core', scale: [0.3, 0.45] },
  ],
  rocks: [
    { model: 'cliff_chunk', r: 2.9, scale: [0.5, 0.9], tint: RIFT_ROCK, weight: 3 },
    { model: 'rift_core', r: 1.2, scale: [0.6, 1.1] },
    { model: 'rock_c', r: 0.7, scale: [1.2, 2.2], tint: RIFT_ROCK },
  ],
  rockSpacing: 2.6,
  clutter: [{ model: 'rock_b', scale: [0.3, 0.55], tint: RIFT_ROCK, weight: 3 }, { model: 'bones' }],
  clutterChance: 0.2,
};

const ground: ZoneLayout['ground'] = ['crystal', 'ash', 'rock', 'dirt'];
/** Rock floor with crystal veins; ash scorches around rift wounds. */
const paint = { floor: 2, path: 3, wall: 2, accent: 0 };

function riftBuilder(floor: 1 | 2 | 3, name: string): DungeonBuilder {
  return new DungeonBuilder(
    {
      id: `rift_${floor}`, name: 'Rift Delve', subtitle: `Floor ${floor} — ${name}`, kind: 'rift', biome: 'rift',
      levelRange: [30, 30], music: 'rift', width: 112, depth: 112, seed: 2500 + floor,
      ground, groundScale: [5, 5, 7, 5], ambient: 'rift', mapTint: 0x24102e,
      wallHeight: 6.5, nearWallHeight: 1.0, accentAmount: 0.75, wallRough: 1.2, paint, kit: KIT,
    },
    new Rng(`rift_${floor}`),
  );
}

/** A dormant rift gate: crystals ringing the spot where the scripted portal opens. */
function portalSite(d: DungeonBuilder, x: number, z: number): void {
  d.spawn('portal', x, z, Math.PI);
  d.paintCircle(x, z, 4.5, 1, 1.5);
  for (const p of DungeonBuilder.ring(x, z, 5.6, 5, 0.6)) d.prop('rift_core', p.x, p.z, { scale: 0.35, rot: p.x });
}

function waves(d: DungeonBuilder, pts: [number, number][]): void {
  pts.forEach(([x, z], i) => d.spawn(`wave_${i}`, x, z, Math.atan2(-x, -z)));
}

/**
 * RIFT DELVE, floor 1 — Riven Hollow. An open cavern of overlapping chambers
 * around a central crystal spire. No packs: the instance script spawns waves
 * at `wave_0..7`; the next-floor portal opens at `portal`.
 */
export function buildRift1(): ZoneLayout {
  const d = riftBuilder(1, 'Riven Hollow');
  d.room(0, 22, 20).room(-20, 0, 17, { y: 0.3 }).room(20, -2, 17, { y: -0.2 }).room(0, -22, 19, { y: 0.2 }).room(0, 0, 14);
  d.room(-30, -20, 9, { y: 0.4 }).room(30, 20, 9, { y: -0.3 });
  d.hole({ kind: 'circle', x: 0, z: 1, r: 4.5 }, 'rock', 3.5);
  d.hole({ kind: 'circle', x: -22, z: 16, r: 3 }, 'rock', 2.5);
  d.hole({ kind: 'circle', x: 21, z: -18, r: 3.2 }, 'rock', 2.5);
  d.prop('rift_core', 0, 1, { scale: 1.5 });
  d.prop('rift_core', -22, 16, { scale: 0.9, rot: 1 });
  d.prop('rift_core', 21, -18, { scale: 1, rot: 2 });

  d.spawn('start', 0, 36, Math.PI);
  portalSite(d, 0, -34);
  waves(d, [[-14, 24], [14, 25], [-28, 2], [28, 0], [-14, -22], [14, -26], [-30, -19], [30, 19]]);
  d.scatterFloor([{ model: 'rock_b', scale: [0.4, 0.8], tint: RIFT_ROCK, noShadow: true }, { model: 'bones', weight: 0.5, noShadow: true }], 22, 5, { margin: 2 });
  return d.build();
}

/**
 * RIFT DELVE, floor 2 — Shattered Galleries. Three chambers joined by wide
 * passages; the floor boss rises in the north chamber (`boss`). Waves at
 * `wave_0..7`, next-floor portal at `portal`.
 */
export function buildRift2(): ZoneLayout {
  const d = riftBuilder(2, 'Shattered Galleries');
  d.room(0, 32, 11).room(-24, 8, 15, { y: 0.4 }).room(24, 10, 14, { y: -0.3 }).room(0, -22, 19, { y: 0.1 });
  d.corridor([{ x: 0, z: 26 }, { x: -18, z: 14 }], 10);
  d.corridor([{ x: 0, z: 26 }, { x: 18, z: 16 }], 10);
  d.corridor([{ x: -20, z: -4 }, { x: -9, z: -15 }], 10);
  d.corridor([{ x: 20, z: -2 }, { x: 9, z: -15 }], 10);
  d.hole({ kind: 'circle', x: -29, z: 3, r: 3 }, 'rock', 2.5);
  d.hole({ kind: 'circle', x: 29, z: 14, r: 2.6 }, 'rock', 2.5);
  d.prop('rift_core', -29, 3, { scale: 0.9 });
  d.prop('rift_core', 29, 14, { scale: 0.8, rot: 2 });
  d.paintRing(0, -22, 9, 10, 1, 0.8);

  d.spawn('start', 0, 38, Math.PI);
  d.spawn('boss', 0, -22, 0);
  portalSite(d, 0, -36);
  waves(d, [[-6, 30], [6, 30], [-28, 12], [-20, 2], [30, 20], [22, 4], [-12, -26], [12, -26]]);
  d.scatterFloor([{ model: 'rock_b', scale: [0.4, 0.8], tint: RIFT_ROCK, noShadow: true }, { model: 'bones', weight: 0.5, noShadow: true }], 20, 5, { margin: 2 });
  return d.build();
}

/**
 * RIFT DELVE, floor 3 — Heart of the Rift. A ring cavern around a raised
 * crystal dais, with five bays holding the breakable rift cores
 * (`core_0..4`). Waves at `wave_0..7`; the final boss spawns at `boss`; the
 * exit portal opens on the dais (`portal`).
 */
export function buildRift3(): ZoneLayout {
  const d = riftBuilder(3, 'Heart of the Rift');
  d.room(0, 0, 36);
  const bays = DungeonBuilder.ring(0, 0, 38, 5, 0);
  bays.forEach((p, i) => d.room(p.x, p.z, 9, { y: i % 2 === 0 ? 0.3 : -0.2 }));
  d.room(0, 44, 7, { y: 0.2 });
  d.corridor([{ x: 0, z: 34 }, { x: 0, z: 42 }], 9);
  // Raised crystal dais in the middle of the ring.
  d.addHeight((x, z) => 0.7 * (1 - smoothstep(7, 9, Math.hypot(x, z))));
  d.paintCircle(0, 0, 8, 0, 1.2);
  d.paintRing(0, 0, 8.4, 9.2, 1, 0.5);
  for (const p of DungeonBuilder.ring(0, 0, 10.5, 6, 1)) d.prop('rift_core', p.x, p.z, { scale: 0.45, rot: p.z, collider: { r: 1.2 } });

  d.spawn('start', 0, 46, Math.PI);
  d.spawn('boss', 0, -16, 0);
  DungeonBuilder.ring(0, 0, 37, 5, 0).forEach((c, i) => {
    d.spawn(`core_${i}`, c.x, c.z, Math.atan2(-c.x, -c.z));
    d.paintCircle(c.x, c.z, 3.4, 1, 1.2);
  });
  portalSite(d, 0, 0);
  const w: [number, number][] = DungeonBuilder.ring(0, 0, 24, 8, 0.75).map((p) => [Math.round(p.x * 10) / 10, Math.round(p.z * 10) / 10]);
  waves(d, w);
  d.scatterFloor([{ model: 'rock_b', scale: [0.4, 0.8], tint: RIFT_ROCK, noShadow: true }, { model: 'bones', weight: 0.5, noShadow: true }], 22, 5, { margin: 2 });
  return d.build();
}
